import React, { useEffect, useRef } from 'react';

import { chargeLevel, cosmosState } from './cosmos';

interface Star {
    angle: number;
    /** Distance from the center, 0 (center) to 1 (the screen's corner). */
    dist: number;
    size: number;
    speed: number;
    twinkle: number;
    gold: boolean;
}

interface Spark {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    gold: boolean;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

const makeStar = (spread = 1): Star => ({
    angle: rand(0, Math.PI * 2),
    dist: Math.sqrt(Math.random()) * spread,
    size: rand(0.5, 1.9),
    speed: rand(0.4, 1.6),
    twinkle: rand(0, Math.PI * 2),
    gold: Math.random() < 0.3,
});

const BURST_SECONDS = 1.6;

/**
 * The backdrop of the whole app: a slow field of stars around a violet glow. While a ritual charges,
 * the stars spiral inward faster and faster, the glow swells, and on `cosmos.burst()` a gold shockwave,
 * a flash and a shower of sparks go off. Fixed behind everything, and ignores touches.
 */
export const CosmicCanvas: React.FC = () => {
    const ref = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const reduced = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        ).matches;
        const stars = Array.from({ length: reduced ? 70 : 150 }, () =>
            makeStar()
        );
        let sparks: Spark[] = [];
        let lastBurst = cosmosState.burstAt;
        let width = 0;
        let height = 0;
        let raf = 0;
        let last = performance.now();

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);

        const frame = (time: number) => {
            const dt = Math.min(0.05, (time - last) / 1000);
            last = time;
            const cx = width / 2;
            const cy = height * 0.42;
            const reach = Math.hypot(width, height) * 0.55;
            const k = chargeLevel();
            const sinceBurst = (time - cosmosState.burstAt) / 1000;
            const bursting = sinceBurst < BURST_SECONDS;

            if (cosmosState.burstAt !== lastBurst) {
                lastBurst = cosmosState.burstAt;
                const count = reduced ? 40 : 140;
                sparks = Array.from({ length: count }, () => {
                    const a = rand(0, Math.PI * 2);
                    const v = rand(120, 620);
                    return {
                        x: cx,
                        y: cy,
                        vx: Math.cos(a) * v,
                        vy: Math.sin(a) * v,
                        life: rand(0.7, 1.6),
                        gold: Math.random() < 0.65,
                    };
                });
            }

            ctx.clearRect(0, 0, width, height);
            const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, reach);
            const glow =
                0.16 +
                k * 0.4 +
                (bursting ? (1 - sinceBurst / BURST_SECONDS) * 0.5 : 0);
            bg.addColorStop(0, `rgba(120, 70, 190, ${glow})`);
            bg.addColorStop(0.45, `rgba(40, 20, 80, ${glow * 0.45})`);
            bg.addColorStop(1, 'rgba(8, 5, 16, 0)');
            ctx.fillStyle = bg;
            ctx.fillRect(0, 0, width, height);

            for (const s of stars) {
                const prevX = cx + Math.cos(s.angle) * s.dist * reach;
                const prevY = cy + Math.sin(s.angle) * s.dist * reach * 0.9;
                s.angle += dt * 0.025 * s.speed * (1 + k * k * 90);
                if (k > 0) s.dist -= dt * k * k * 0.9 * (0.3 + s.dist);
                if (bursting)
                    s.dist +=
                        dt * (1 - sinceBurst / BURST_SECONDS) * 1.4 * s.dist;
                if (s.dist < 0.02 || s.dist > 1.15) {
                    Object.assign(s, makeStar(), {
                        dist: k > 0 ? rand(0.7, 1.05) : rand(0.05, 1),
                    });
                }
                s.twinkle += dt * 2 * s.speed;
                const x = cx + Math.cos(s.angle) * s.dist * reach;
                const y = cy + Math.sin(s.angle) * s.dist * reach * 0.9;
                const alpha = 0.35 + 0.35 * Math.sin(s.twinkle) + k * 0.3;
                const color = s.gold ? '240, 205, 130' : '200, 190, 255';
                if (k > 0.05 || bursting) {
                    ctx.strokeStyle = `rgba(${color}, ${Math.min(1, alpha)})`;
                    ctx.lineWidth = s.size;
                    ctx.beginPath();
                    ctx.moveTo(prevX, prevY);
                    ctx.lineTo(x, y);
                    ctx.stroke();
                } else {
                    ctx.fillStyle = `rgba(${color}, ${Math.max(0.05, alpha)})`;
                    ctx.beginPath();
                    ctx.arc(x, y, s.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            if (bursting) {
                const p = sinceBurst / BURST_SECONDS;
                for (let ring = 0; ring < 3; ring++) {
                    const rp = Math.max(0, p - ring * 0.08);
                    ctx.strokeStyle = `rgba(240, 205, 130, ${(1 - rp) * 0.7})`;
                    ctx.lineWidth = 3 - ring;
                    ctx.beginPath();
                    ctx.arc(cx, cy, rp * reach * 1.2, 0, Math.PI * 2);
                    ctx.stroke();
                }
                if (p < 0.25) {
                    ctx.fillStyle = `rgba(255, 244, 220, ${(1 - p / 0.25) * 0.55})`;
                    ctx.fillRect(0, 0, width, height);
                }
            }

            sparks = sparks.filter((sp) => sp.life > 0);
            for (const sp of sparks) {
                sp.x += sp.vx * dt;
                sp.y += sp.vy * dt;
                sp.vx *= 0.985;
                sp.vy = sp.vy * 0.985 + 30 * dt;
                sp.life -= dt;
                ctx.fillStyle = sp.gold
                    ? `rgba(245, 214, 140, ${Math.min(1, sp.life)})`
                    : `rgba(190, 160, 255, ${Math.min(1, sp.life)})`;
                ctx.beginPath();
                ctx.arc(sp.x, sp.y, 1.6 + sp.life, 0, Math.PI * 2);
                ctx.fill();
            }

            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <canvas
            ref={ref}
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                pointerEvents: 'none',
            }}
        />
    );
};
